import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import React from "react";
import clsx from "clsx";
import { shadowAndBorderClass } from "screens/Dashboard/utils/classes";

interface AnimatedProgressCardProps {
  currentRaised: number;
  totalTarget: number;
  progress: number;
}

const fadeInAndScale = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { delay: 0.5, duration: 0.6 },
};

export const customShadow = `shadow-[0px_6px_7.5px_0px_#0000000F] max-lg:shadow-[0px_6px_7.5px_0px_#0000000F]`;

export const AnimatedProgressCard: React.FC<AnimatedProgressCardProps> = ({
  currentRaised,
  totalTarget,
  progress,
}) => {
  const { t } = useTranslation();

  return (
    <motion.div {...fadeInAndScale} className="w-full">
      <div className={clsx(
        "relative flex items-start gap-[24px] max-lg:gap-[24px] px-[12px] max-lg:px-[12px] py-[8px] max-lg:py-[8px] w-full rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px] bg-[#161616]",
        shadowAndBorderClass
      )}>
          <div className="flex flex-col items-start gap-[12px] max-lg:gap-[12px] px-0 py-[4px] max-lg:py-[4px] flex-1 relative z-[2]">
            <div className="flex items-start font-luxora-regular justify-between w-full gap-[8px] max-lg:gap-[8px]">
              <div className="flex-1 mt-[-1px] max-lg:mt-[-1px] text-white/40 text-[16px] max-lg:text-[14px] leading-[18px] max-lg:leading-[18px]">
                {t("USD Raised")}
              </div>
              <motion.div
                className="w-fit mt-[-1px] max-lg:mt-[-1px] text-white text-[16px] max-lg:text-[14px] leading-[18px] max-lg:leading-[18px] whitespace-nowrap font-medium"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                $
                {currentRaised.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                / $
                {totalTarget.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </motion.div>
            </div>

            <div
              className={`relative w-full h-[12px] p-[2px] max-lg:p-[2px] max-lg:h-[12px] bg-[#2a2a2a] rounded-full overflow-hidden ${customShadow}`}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 2, ease: "easeInOut" }}
                className="h-full rounded-full relative overflow-hidden"
                style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)" }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  style={{ width: "40%" }}
                  animate={{ x: ["-100%", "300%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "loop",
                    ease: "easeInOut",
                    repeatDelay: 0.5,
                  }}
                />
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  style={{ width: "60%" }}
                  animate={{ x: ["-150%", "250%"] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    repeatType: "loop",
                    ease: "linear",
                    delay: 1,
                  }}
                />
              </motion.div>
            </div>
          </div>
      </div>
    </motion.div>
  );
};
